import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import { Bar } from 'react-chartjs-2';
import { shuffle } from 'lodash';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);
import ErrorBoundary from 'components/ErrorBoundary';
import CustomeDate from './customeDate';

const getFirstData = (month) => {
  switch (month) {
    case '3':
      return [215124, 101225, 85223, 62354];
    case '6':
      return [101225, 62354, 215124, 85223];
    case '12':
      return [62354, 85223, 101225, 215124];
    default:
      break;
  }
};

const topDrugs = [
  {
    ndc: 169413013,
    ce: 1,
    label: 'OZEMPIC 1 MG/DOSE (4 MG/3 ML)',
    count: 4000,
    saving: 155954,
  },
  {
    ndc: 169413212,
    ce: 2,
    label: 'OZEMPIC 0.25-0.5 MG/DOSE PEN',
    count: 1000,
    saving: 124000,
  },
  {
    ndc: 169750111,
    ce: 3,
    label: 'NOVOLOG 100 UNIT/ML VIAL',
    count: 523,
    saving: 110000,
  },
  {
    ndc: 61874011530,
    ce: 4,
    label: 'VRAYLAR 1.5 MG CAPSULE',
    count: 659,
    saving: 98456,
  },
  {
    ndc: 169255013,
    ce: 5,
    label: 'TRESIBA FLEXTOUCH 200 UNIT/ML',
    count: 256,
    saving: 89254,
  },
  {
    ndc: 57841130101,
    ce: 6,
    label: 'MOVANTIK 25 MG TABLET',
    count: 214,
    saving: 75412,
  },
  {
    ndc: 469260290,
    ce: 7,
    label: 'MYRBETRIQ ER 50 MG TABLET',
    count: 854,
    saving: 65123,
  },
  {
    ndc: 69024230,
    ce: 8,
    label: 'TOVIAZ ER 4 MG TABLET',
    count: 526,
    saving: 64145,
  },
  {
    ndc: 3089421,
    ce: 9,
    label: 'ELIQUIS 5 MG TABLET',
    count: 1456,
    saving: 62145,
  },
  {
    ndc: 169643810,
    ce: 10,
    label: 'LEVEMIR FLEXTOUCH 100 UNIT/ML',
    count: 1487,
    saving: 59997,
  },
];
const externalTooltipHandler = (context, data) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set Text
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const title = titleLines[0];
    const chartRowData = data.filter((item) => item.label === title);
    console.log({ chartRowData });

    const bodyLines = tooltip.body.map((b) => b.lines);

    const tableHead = document.createElement('thead');

    titleLines.forEach((title) => {
      const tr = document.createElement('tr');
      tr.style.borderWidth = 0;

      const th = document.createElement('th');
      th.style.borderWidth = 0;
      const text = document.createTextNode(title);

      const ndc = document.createElement('th');
      ndc.style.borderWidth = 0;
      const ndcText = document.createTextNode(chartRowData[0].ndc);

      th.appendChild(text);
      ndc.appendChild(ndcText);
      tr.appendChild(ndc);
      tr.appendChild(th);
      tableHead.appendChild(tr);
    });

    const tableBody = document.createElement('tbody');
    bodyLines.forEach((body, i) => {
      const colors = tooltip.labelColors[i];

      const span = document.createElement('span');
      span.style.background = colors.backgroundColor;
      span.style.borderColor = colors.borderColor;
      span.style.borderWidth = '2px';
      span.style.marginRight = '10px';
      span.style.height = '10px';
      span.style.width = '10px';
      span.style.display = 'inline-block';

      const tr = document.createElement('tr');
      tr.style.backgroundColor = 'inherit';
      tr.style.borderWidth = 0;

      const td = document.createElement('td');
      td.style.borderWidth = 0;

      const text = document.createTextNode(body);

      const rxCount = document.createElement('td');
      rxCount.style.borderWidth = 0;

      const rxCountText = document.createTextNode(
        `Rx Count: ${chartRowData[0].count}`
      );

      td.appendChild(span);
      td.appendChild(text);
      tr.appendChild(td);
      rxCount.appendChild(span);
      rxCount.appendChild(rxCountText);
      tr.appendChild(rxCount);
      tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector('table');

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = positionX + tooltip.caretX + 'px';
  tooltipEl.style.top = positionY + tooltip.caretY + 'px';
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding =
    tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
};

const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
    tooltipEl.style.borderRadius = '3px';
    tooltipEl.style.color = 'white';
    tooltipEl.style.opacity = 1;
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.transition = 'all .1s ease';

    const table = document.createElement('table');
    table.style.margin = '0px';

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const TopDrugs = () => {
  const [data, setData] = useState(topDrugs);
  const [active, setActive] = useState('12');
  const labels = data.map(({ label }) => label);
  const options = {
    maintainAspectRatio: false,
    indexAxis: 'y',
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
    responsive: true,
    plugins: {
      tooltip: {
        enabled: true,
        position: 'nearest',
        callbacks: {
          title: function (item, index) {
            const { count, ndc, label } = data[item[0].dataIndex];
            return `NDC# ${ndc}\n${label} \nRx Count ${count}`;
          },
          label: function (d,t) {
            var xLabel = d.dataset.label;
            var yLabel ='$' + d.formattedValue;
            return xLabel + ': ' + yLabel;
          },
        },
        // external: (context) => externalTooltipHandler(context, data),
      },
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        align: function (context) {
          return context.dataset.data[context.dataIndex] < 150000
            ? 'end'
            : 'start';
        },
        color: function (context) {
          return context.dataset.data[context.dataIndex] < 150000
            ? 'black'
            : 'black';
        },
        anchor: 'end',
        padding: {
          right: 2,
        },
        labels: {
          padding: { top: 10 },
          value: {
            color: '#2B3346',
          },
        },
        formatter: function (value) {
          return (
            '$' + value.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
          );
        },
      },
    },
  };
  const chartData = {
    labels,
    datasets: [
      {
        label: 'Program Value',
        data: data.map(({ saving }) => saving),
        borderColor: 'rgba(18, 84, 94, 1)',
        backgroundColor: 'rgba(18, 84, 94, 1)',
      },
    ],
  };
  const handleActive = (title) => {
    if (title === active) return;
    setActive(title);
    const shuffledArry = shuffle(data);
    setData(shuffledArry);
  };

  const getDate = () => {
    setActive('custom')
  };

  return (
    <ErrorBoundary>
      <Card className='p-3 h-100'>
        <CardHeader className='border-0 bg-white px-0'>
          <Row className='align-items-center'>
            <Col>
              <CardTitle className='f-20 font-weight-500 mb-0'>
                Top 10 Drugs
                <p className='dashboard-sub-text'>By Program Value</p>
              </CardTitle>
            </Col>
            <Col xs='auto'>
              <Nav className='widget-tabs'>
                <NavItem>
                  <NavLink
                    className={
                      active === '3'
                        ? 'active-tab f-12 cursor-pointer'
                        : 'f-12 cursor-pointer'
                    }
                    onClick={() => handleActive('3')}
                  >
                    3 Mon
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={
                      active === '6'
                        ? 'active-tab f-12 cursor-pointer'
                        : 'f-12 cursor-pointer'
                    }
                    onClick={() => handleActive('6')}
                  >
                    6 Mon
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={
                      active === '12'
                        ? 'active-tab f-12 cursor-pointer'
                        : 'f-12 cursor-pointer'
                    }
                    onClick={() => handleActive('12')}
                  >
                    1 Yr
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={
                      active === 'custom'
                        ? 'active-tab f-12 cursor-pointer'
                        : 'f-12 cursor-pointer'
                    }>
                    <CustomeDate getDate={getDate} active={active} />
                  </NavLink>
                </NavItem>
              </Nav>
            </Col>
          </Row>
        </CardHeader>
        <CardBody className='dashboard-chart-body'>
          <div className='dashboard-chart-height-BITool'>
            <Bar
              options={options}
              data={chartData}
              plugins={[ChartDataLabels]}
            />
          </div>
          <div className='view-report'>
            <a
              href="data:text/csv;charset=utf-8,'+escape(csv)+'"
              download='filename.csv'
            >
              {' '}
              View Report{' '}
            </a>
          </div>
        </CardBody>
      </Card>
    </ErrorBoundary>
  );
};

export default TopDrugs;
